import {
  Component,
  DestroyRef,
  Input,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ViaQuestion } from '@cpd-app/core-module/models';
import { AiChatFacade } from '@cpd-app/state/ai-chat';
import { CognitoAuthFacade } from '@cpd-app/state/cognito-auth';
import { UserProfileResponse } from '@cpd-app/state/user-profile/user-profile.response';
import { fromEvent } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

import { LoginDialogComponent } from '../login-dialog/login-dialog.component';
import { AccountCreationPromptService } from '@cpd-app/core-module/services';

@Component({
  selector: 'cpd-via-button',
  templateUrl: './via-button.component.html',
  styleUrls: ['./via-button.component.scss']
})
export class ViaButtonComponent implements OnInit {
  @ViewChild('chatAccessTooltip')
  public chatAccessTooltipTemplate: TemplateRef<HTMLDivElement>;

  /**
   * MOBILE.1 R2. `fab` is the floating circle; `tab` is the flat Ask Via item
   * in the phone bottom bar. Same behaviour either way, including answering
   * the app's "Ask Via" pills, which is why the bar uses this component
   * rather than a copy of it.
   */
  @Input() public variant: 'fab' | 'tab' = 'fab';

  public tooltipRef: MatDialogRef<HTMLDivElement>;
  public highlight = false;

  constructor(
    private dialog: MatDialog,
    private destroyRef: DestroyRef,
    private userProfileResponse: UserProfileResponse,
    private cognitoAuthFacade: CognitoAuthFacade,
    private accountCreationPromptService: AccountCreationPromptService,
    private aiChatFacade: AiChatFacade
  ) {}

  public ngOnInit(): void {
    this.listenToSkillsSurveyComplete();
    this.listenToOpenChatVia();
  }

  public toggleChat(): void {
    this.handleOpenChat();
  }

  private handleOpenChat(question?: ViaQuestion): void {
    this.cognitoAuthFacade.isAuthenticated$
      .pipe(take(1))
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          this.aiChatFacade.openChatVia();

          if (question) {
            this.handleSendQuestion(question);
          }
          return;
        }
        this.accountCreationPromptService.openAccountCreationDialog(
          'skills-survey'
        );
      });
  }

  public openLoginDialog(): void {
    this.dialog.closeAll();
    this.dialog.open(LoginDialogComponent, { ariaLabel: 'Sign in' });
  }

  private listenToOpenChatVia(): void {
    this.aiChatFacade.askQuestion$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ question }) => {
        this.handleOpenChat(question);
      });
  }

  private listenToSkillsSurveyComplete(): void {
    this.userProfileResponse.isSkillsSurveyComplete$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        // L3 (live test 30 Sep): the nudge is for a guest. This path had no
        // account check at all, so a signed-in member saving the Skills
        // Survey was told to "Unlock Via with an account".
        switchMap(() => this.cognitoAuthFacade.isAuthenticated$.pipe(take(1)))
      )
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          return;
        }
        this.openTooltip(this.chatAccessTooltipTemplate);
        this.highlight = true;
      });
  }

  /**
   * MOBILE.1 D7. The nudge never stacks on another dialog.
   *
   * It is itself a dialog, and it used to open whatever else was showing: a
   * signed-out member tapping Via got the sign-in dialog with the green
   * "Unlock Via with an account" box on top of its form. It says nothing the
   * sign-in dialog does not, so it gives way: it does not open over another
   * dialog, and it closes when one opens after it.
   */
  private openTooltip(popupContent: TemplateRef<HTMLDivElement>): void {
    if (this.dialog.openDialogs.length > 0) {
      return;
    }

    this.tooltipRef = this.dialog.open(popupContent, {
      hasBackdrop: false,
      // Above whichever button raised it: the circle, or the bar.
      position:
        this.variant === 'tab'
          ? { bottom: '72px', left: '0px' }
          : { bottom: '90px', left: '0px' }
    });

    this.tooltipRef.afterOpened().subscribe(() => {
      this.handleCloseTooltip();
    });

    const nudge = this.tooltipRef;
    this.dialog.afterOpened
      .pipe(
        filter((opened) => opened !== nudge),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        nudge.close();
        this.highlight = false;
      });
  }

  private handleCloseTooltip(): void {
    fromEvent(window, 'click')
      .pipe(take(1))
      .subscribe(() => {
        this.tooltipRef.close();
        this.highlight = false;
      });
  }

  private handleSendQuestion(question: ViaQuestion): void {
    if (question && question.query !== 'Ask something else') {
      this.aiChatFacade.streamStart(question.query, question);
    }
  }
}
